/**
 * Dynamic Question Selection System
 * 
 * This utility provides a flexible way to manage question ordering
 * with fixed and random selection capabilities.
 */

/**
 * Generate a random question order based on configuration
 * @param {Array} questionSet - Array of all available questions
 * @param {Object} config - Configuration object
 * @returns {Array} Selected questions in order
 */
export const generateQuestionOrder = (questionSet, config) => {
  const { fixedQuestions, randomQuestions } = config;
  
  // Validate configuration
  if (fixedQuestions + randomQuestions > questionSet.length) {
    throw new Error('Configuration exceeds available questions');
  }
  
  // Get fixed questions (first N questions in order)
  const fixed = questionSet.slice(0, fixedQuestions);
  
  // Get remaining questions for random selection
  const remaining = questionSet.slice(fixedQuestions);
  
  // Shuffle remaining questions and select required number
  const shuffled = [...remaining].sort(() => Math.random() - 0.5);
  const random = shuffled.slice(0, randomQuestions);
  
  return [...fixed, ...random];
};

/**
 * Create a question selector with predefined configuration
 * @param {Array} questionSet - Array of all available questions
 * @param {Object} config - Configuration object
 * @returns {Function} Function that generates question order
 */
export const createQuestionSelector = (questionSet, config) => {
  return () => generateQuestionOrder(questionSet, config);
};

/**
 * Validate question configuration
 * @param {Array} questionSet - Array of all available questions
 * @param {Object} config - Configuration object
 * @returns {Object} Validation result
 */
export const validateQuestionConfig = (questionSet, config) => {
  const { fixedQuestions, randomQuestions, totalQuestions } = config;
  
  const errors = [];
  const warnings = [];
  
  // Check if total questions match
  if (fixedQuestions + randomQuestions !== totalQuestions) {
    errors.push('fixedQuestions + randomQuestions must equal totalQuestions');
  }
  
  // Check if we have enough questions
  if (totalQuestions > questionSet.length) {
    errors.push(`totalQuestions (${totalQuestions}) exceeds available questions (${questionSet.length})`);
  }
  
  // Check if we have enough questions for random selection
  if (randomQuestions > questionSet.length - fixedQuestions) {
    errors.push(`randomQuestions (${randomQuestions}) exceeds remaining questions (${questionSet.length - fixedQuestions})`);
  }
  
  // Warnings
  if (randomQuestions === 0) {
    warnings.push('No random questions selected - all questions will be in fixed order');
  }
  
  if (fixedQuestions === 0) {
    warnings.push('No fixed questions - all questions will be randomly ordered');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Get question selection statistics
 * @param {Array} questionSet - Array of all available questions
 * @param {Array} selectedQuestions - Array of selected questions
 * @returns {Object} Statistics about the selection
 */
export const getSelectionStats = (questionSet, selectedQuestions) => {
  const fixedQuestions = selectedQuestions.slice(0, 2); // Assuming first 2 are fixed
  const randomQuestions = selectedQuestions.slice(2);
  
  return {
    totalAvailable: questionSet.length,
    totalSelected: selectedQuestions.length,
    fixedQuestions: fixedQuestions.length,
    randomQuestions: randomQuestions.length,
    unusedQuestions: questionSet.filter(q => !selectedQuestions.includes(q)),
    selectionRatio: selectedQuestions.length / questionSet.length
  };
};

/**
 * Example usage and configuration
 */
export const EXAMPLE_CONFIG = {
  // Question sets for different modules
  FARMER_VERIFICATION: [
    "fv_land_type",
    "fv_crop_type", 
    "fv_crop_pattern",
    "fv_farm_challenge",
    "fv_irrigation_timing",
    "fv_residue_practice"
  ],
  
  // Configuration templates
  CONFIGS: {
    // Fixed first 2, random next 2
    FIXED_THEN_RANDOM: {
      fixedQuestions: 2,
      randomQuestions: 2,
      totalQuestions: 4
    },
    
    // All random
    ALL_RANDOM: {
      fixedQuestions: 0,
      randomQuestions: 4,
      totalQuestions: 4
    },
    
    // All fixed
    ALL_FIXED: {
      fixedQuestions: 4,
      randomQuestions: 0,
      totalQuestions: 4
    }
  }
};

export default {
  generateQuestionOrder,
  createQuestionSelector,
  validateQuestionConfig,
  getSelectionStats,
  EXAMPLE_CONFIG
};

